import { RadioCardOption } from "components/form/RadioCardField";
import OversizedFlyerTemplate from "static/files/Oversized_12x15_postcard_template.zip";
import ADMPostcard6x4Templates from "static/files/postcard-4x6-templates.zip";
import ADMPostcard9x6Templates from "static/files/postcard-6x9-templates.zip";
import ADMPostcard11x6Templates from "static/files/postcard-6x11-templates.zip";
import AdmAuA6Template from "static/files/ADM_AU_A6_template.pdf";
import AdmAuDlTemplate from "static/files/ADM_AU_DL_template.pdf";
import AdmAuA5Template from "static/files/ADM_AU_A5_template.pdf";
import AdmAuA4Template from "static/files/ADM_AU_A4_template.pdf";
import postcardImage from "static/media/format-postcard.svg";
import letterImage from "static/media/format-letter.svg";
import wideLetterImage from "static/media/format-wide-letter.svg";
import flatImage from "static/media/format-flat.svg";

const HEADER_TITLE = {
  DESIGN: "Design",
  SEGMENTS: "Segments",
  SUBMIT: "Submit",
};

const PAGE_TITLE = {
  DESIGN: "Now, select your design",
  SEGMENTS: "Define the audience for your campaign",
  SUBMIT: "Submit",
};

export const ADM_STEP = {
  SEGMENTS: "segments",
  DESIGN: "design",
  SUBMIT: "submit",
};

export interface NavigationItem {
  title: string;
  pageTitle: string;
  step: string;
  index: number;
}

export const ADM_NAVIGATION: NavigationItem[] = [
  {
    title: HEADER_TITLE.SEGMENTS,
    pageTitle: PAGE_TITLE.SEGMENTS,
    step: ADM_STEP.SEGMENTS,
    index: 1,
  },
  {
    title: HEADER_TITLE.DESIGN,
    pageTitle: PAGE_TITLE.DESIGN,
    step: ADM_STEP.DESIGN,
    index: 2,
  },
  {
    title: HEADER_TITLE.SUBMIT,
    pageTitle: PAGE_TITLE.SUBMIT,
    step: ADM_STEP.SUBMIT,
    index: 3,
  },
];

export enum ADM_FORMAT {
  POSTCARD_6x4 = "POSTCARD_6x4",
  POSTCARD_9x6 = "POSTCARD_9x6",
  POSTCARD_11x6 = "POSTCARD_11x6",
  POSTCARD_15x12 = "POSTCARD_15x12",
  // International launch catalogue (metric, flat pieces) — offered per country
  // via getFormatsFor("ADM", countryCode); values double as the
  // CountryADMPrintingCosts label key, like the US postcards above.
  A6 = "A6",
  DL = "DL",
  A5 = "A5",
  A4 = "A4",
}

export const FORMAT_OPTIONS: RadioCardOption[] = [
  {
    value: ADM_FORMAT.POSTCARD_6x4,
    label: '4" x 6"',
    topText: "Postcard",
    bottomText: "$",
    image: postcardImage,
  },
  {
    value: ADM_FORMAT.POSTCARD_9x6,
    label: '6" x 9"',
    topText: "Postcard",
    bottomText: "$$",
    image: letterImage,
  },
  {
    value: ADM_FORMAT.POSTCARD_11x6,
    label: '6" x 11"',
    topText: "Postcard",
    bottomText: "$$$",
    image: wideLetterImage,
  },
  {
    value: ADM_FORMAT.POSTCARD_15x12,
    label: '12" x 15"',
    topText: "Postcard",
    bottomText: "$$$$",
    image: flatImage,
  },
  {
    value: ADM_FORMAT.A6,
    label: "14.8 × 10.5 cm",
    topText: "A6",
    bottomText: "$",
    image: postcardImage,
  },
  {
    value: ADM_FORMAT.DL,
    label: "21.0 × 9.9 cm",
    topText: "DL",
    bottomText: "$$",
    image: wideLetterImage,
  },
  {
    value: ADM_FORMAT.A5,
    label: "21.0 × 14.8 cm",
    topText: "A5",
    bottomText: "$$$",
    image: letterImage,
  },
  {
    value: ADM_FORMAT.A4,
    label: "21.0 × 29.7 cm",
    topText: "A4",
    bottomText: "$$$$",
    image: flatImage,
  },
];

export const ADM_FORMAT_PREVIEW = {
  [ADM_FORMAT.POSTCARD_6x4]: {
    width: 700,
    height: 466,
    thumbnailWidth: 184,
    thumbnailHeight: 122,
    widthInInch: 6,
    heightInInch: 4,
  },
  [ADM_FORMAT.POSTCARD_9x6]: {
    width: 700,
    height: 466,
    thumbnailWidth: 184,
    thumbnailHeight: 122,
    widthInInch: 9,
    heightInInch: 6,
  },
  [ADM_FORMAT.POSTCARD_11x6]: {
    width: 700,
    height: 381,
    thumbnailWidth: 184,
    thumbnailHeight: 100,
    widthInInch: 11,
    heightInInch: 6,
  },
  [ADM_FORMAT.POSTCARD_15x12]: {
    width: 700,
    height: 560,
    thumbnailWidth: 160,
    thumbnailHeight: 122,
    widthInInch: 15,
    heightInInch: 12,
  },
  // Metric formats (mm → in): A6 148×105, DL 210×99, A5 210×148, A4 210×297.
  // A4 is the one portrait piece in the catalogue.
  [ADM_FORMAT.A6]: {
    width: 700,
    height: 497,
    thumbnailWidth: 184,
    thumbnailHeight: 131,
    widthInInch: 5.83,
    heightInInch: 4.13,
  },
  [ADM_FORMAT.DL]: {
    width: 700,
    height: 330,
    thumbnailWidth: 184,
    thumbnailHeight: 87,
    widthInInch: 8.27,
    heightInInch: 3.9,
  },
  [ADM_FORMAT.A5]: {
    width: 700,
    height: 493,
    thumbnailWidth: 184,
    thumbnailHeight: 130,
    widthInInch: 8.27,
    heightInInch: 5.83,
  },
  [ADM_FORMAT.A4]: {
    width: 700,
    height: 990,
    thumbnailWidth: 86,
    thumbnailHeight: 122,
    widthInInch: 8.27,
    heightInInch: 11.69,
  },
};

// Trim size in mm for the metric (international) formats — the source of
// truth for artwork dimension validation (PAD-32228). US postcards are not
// validated (legacy behavior); only formats listed here are checked.
export const ADM_FORMAT_TRIM_MM: Partial<Record<ADM_FORMAT, { widthMm: number; heightMm: number }>> = {
  [ADM_FORMAT.A6]: { widthMm: 148, heightMm: 105 },
  [ADM_FORMAT.DL]: { widthMm: 210, heightMm: 99 },
  [ADM_FORMAT.A5]: { widthMm: 210, heightMm: 148 },
  [ADM_FORMAT.A4]: { widthMm: 210, heightMm: 297 },
};

export const ADM_FORMATS_TEMPLATES = {
  [ADM_FORMAT.POSTCARD_6x4]: ADMPostcard6x4Templates,
  [ADM_FORMAT.POSTCARD_9x6]: ADMPostcard9x6Templates,
  [ADM_FORMAT.POSTCARD_11x6]: ADMPostcard11x6Templates,
  [ADM_FORMAT.POSTCARD_15x12]: OversizedFlyerTemplate,
  // International templates are per country × format; the metric formats are
  // AU-only in v1, so a format-keyed map still resolves correctly (PAD-32227).
  // Revisit the keying when a second intl country ships.
  [ADM_FORMAT.A6]: AdmAuA6Template,
  [ADM_FORMAT.DL]: AdmAuDlTemplate,
  [ADM_FORMAT.A5]: AdmAuA5Template,
  [ADM_FORMAT.A4]: AdmAuA4Template,
};

export const MINIMUM_BUSINESS_DAYS = 5;
